// propose_clarify envelopes: the single-question picker and the structured
// form variant.

import type {
  BlockOutput,
  ClarifyBlockData,
  ClarifyFieldSearch,
} from '../../types';
import { arr, num, str } from './guards';
import type { StructuredClarify, StructuredClarifyForm } from './types';

const asOptions = (raw: unknown): { value: string; label: string }[] =>
  arr(raw)
    .map((o) => {
      if (typeof o !== 'object' || o === null) return null;
      const obj = o as { value?: unknown; label?: unknown };
      const value = str(obj.value) ?? '';
      const label = str(obj.label) ?? '';
      if (!value || !label) return null;
      return { value, label };
    })
    .filter((o): o is { value: string; label: string } => o !== null);

// On-the-fly search descriptor (unbounded pickers: users, projects).
const asFieldSearch = (raw: unknown): ClarifyFieldSearch | undefined => {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const s = raw as {
    tool?: unknown;
    input?: unknown;
    label_column?: unknown;
    value_prefix?: unknown;
  };
  const tool = str(s.tool);
  if (!tool) return undefined;
  return {
    tool,
    input:
      typeof s.input === 'object' && s.input !== null
        ? (s.input as Record<string, unknown>)
        : undefined,
    labelColumn: str(s.label_column),
    valuePrefix: str(s.value_prefix),
  };
};

export const buildClarifyBlock = (
  t: StructuredClarify,
  toolCallId?: string
): BlockOutput | null => {
  const intro = str(t.intro) ?? '';
  const pending = str(t.pending) ?? '';
  if (!intro || !pending) return null;
  const options = asOptions(t.options);
  if (options.length === 0) return null;
  const data: ClarifyBlockData = {
    intro,
    pending,
    options,
    submitLabel: str(t.submit_label),
    toolCallId,
  };
  return { type: 'clarify', data };
};

export const buildClarifyFormBlock = (
  t: StructuredClarifyForm,
  toolCallId?: string
): BlockOutput | null => {
  const intro = str(t.intro) ?? '';
  // The `{key}` template: backend calls it `pending`; tolerate the old `warning`.
  const template = str(t.pending) ?? str(t.warning) ?? '';
  if (!intro || !template) return null;
  // Backend emits `questions`; tolerate the old `actions`.
  const rawQuestions = Array.isArray(t.questions)
    ? t.questions
    : Array.isArray(t.actions)
      ? t.actions
      : [];
  const actions = rawQuestions
    .map((a) => {
      if (typeof a !== 'object' || a === null) return null;
      const obj = a as Record<string, unknown>;
      const key = str(obj.key) ?? '';
      const label = str(obj.label) ?? '';
      if (!key || !label) return null;
      const options = asOptions(obj.options);
      return {
        key,
        label,
        help: str(obj.help),
        prefill: str(obj.prefill) ?? '',
        placeholder: str(obj.placeholder),
        required: obj.required === true,
        priority: num(obj.priority) ?? 0,
        type: str(obj.type) ?? (options.length > 0 ? 'picker' : 'text'),
        options,
        selected: str(obj.selected) ?? '',
        multi: obj.multi === true,
        search: asFieldSearch(obj.search),
      };
    })
    .filter((a): a is NonNullable<typeof a> => a !== null)
    .sort((a, b) => a.priority - b.priority);
  if (actions.length === 0) return null;
  return {
    type: 'clarifyForm',
    data: {
      intro,
      warning: template,
      actions,
      submitLabel: str(t.submit_label) || str(t.it_label) || 'Continue',
      toolCallId,
    },
  };
};
